























import { $, versionCompare, removeElement, sleep } from '@/utils/helpers'
import { mapMutations, mapActions, mapGetters } from 'vuex'
import MessageMixin from '@/mixins/MessageMixin'
import mixins from 'vue-typed-mixins'
import push from '@/push-notification'
import AppFooter from '@/components/AppFooter.vue'
import config from '@/config'

/**
 * @vue App
 */
export default mixins(MessageMixin).extend({
  name: 'app',
  components: {
    AppFooter
  },
  data() {
    return {
      alertCheckAppVersion: null,
      alertCheckRejectAndroid: null,
      routerViewKey: '',
      isShowLoading: false,
      loadingTimer: null
    }
  },
  computed: {
    ...mapGetters('Auth', ['token', 'isBan']),
    ...mapGetters('AppInfo', ['appName']),

    useEdgeSwipe(): boolean {
      return config.FLAG_EDGE_SWIPE && this.$route.name !== 'call'
    },

    promiseSkip(): string {
      return 'PROMISE_SKIP'
    }
  },
  created() {
    // 起動直後は resume は呼び出されないので明示的にアクティブフラグをセット
    sessionStorage.setItem('isActiveApp', '1')

    // token の有効判定
    this.verifyToken()
      .then(verify => {
        if (!this.$_hasCordova || !verify) {
          return Promise.reject(this.promiseSkip)
        }

        // アプリで token が有効なら端末にアカウント情報が保存されているかチェック
        return this.checkHasAuthInfoOnDevice()
      })
      .then(hasAuthInfo => {
        if (hasAuthInfo) {
          return Promise.reject(this.promiseSkip)
        }

        // 端末にアカウント情報がないならサーバから取得
        return this.$store.dispatch('Auth/fetchAuthInfo')
      })
      .then(({ user_id, password, history_id }) => {
        // 端末に保存
        return this.$_secureSaveAccount({
          userId: user_id,
          password: password,
          historyId: history_id
        })
      })
      .catch(err => {
        if (err === 'PROMISE_SKIP') return

        console.error(err)
      })

    if (this.$_hasCordova) {
      this.checkAppVersion()
      this.checkRejectAndroid()

      // Cordova イベントキャッチ
      document.addEventListener(
        'pause',
        () => {
          console.log('[pause]')
          sessionStorage.removeItem('isActiveApp')

          // iPhone で着信履歴から起動した状態でホームに戻ると上部に「通話中」と表示されるのを防ぐ
          if (this.$_platformIsIOS) {
            cordova.plugins.CordovaCall.endCall()
          }
          this.$root.$emit('pause')
        },
        false
      )
      document.addEventListener(
        'resume',
        () => {
          console.log('[resume]')
          sessionStorage.setItem('isActiveApp', '1')

          // アクティブにバッジクリア
          cordova.plugins.notification.badge.clear()

          if (this.$_platformIsAndroid) {
            // デフォルトはタスク一覧に表示する
            cordova.plugins.TaskList.includeInTaskList()
          }

          this.checkAppVersion()
          this.checkRejectAndroid()
          this.checkHasUnreadMessage()
          this.checkHasUnreadNotice()

          this.$root.$emit('resume')
        },
        false
      )
      // NOTE: backbutton イベントは個別でセットしないとバック処理に管理が大変になる

      // token が有効かつログイン済みならプッシュ通知初期化
      if (this.token) {
        // Push 通知の初期化はログイン後にしないと device_token が上書きされない
        push.initPushNotification(this.$root)
        // ios9 最新バージョン 9.3.5 より上なら iOS10 と判断
        if (this.$_platformIsIOS && versionCompare(device.version, '9.3.5')) {
          push.initVoIPPushNotification(this.$root)
        }
      }
    }

    this.checkHasUnreadMessage()
    this.checkHasUnreadNotice()

    this.$root.$on('routerViewKeyChange', this.handleRouterViewKeyChange)
    this.$root.$on('receivedChat', this.receivedChat)
    this.$root.$on('receivedTimelineIine', this.receivedTimelineIine)
    this.$root.$on('receivedNotice', this.receivedNotice)
  },
  mounted() {
    this.$nextTick().then(() => {
      // 表示に時間がかかる端末用の読み込みラベルを削除
      let beforeInitialLoading = $('.before-initial-loading')
      if (beforeInitialLoading) {
        removeElement(beforeInitialLoading)
        beforeInitialLoading = null
      }
    })
  },
  methods: {
    ...mapMutations('MessageList', ['setHasMessageBadge']),
    ...mapMutations('Notice', ['setHasNoticeBadge']),
    ...mapActions('MessageList', ['checkHasUnreadMessage', 'sortMessages']),
    ...mapActions('Notice', ['checkHasUnreadNotice']),

    /**
     * トークンの有効判定
     */
    verifyToken(): Promise<boolean> {
      return this.$store
        .dispatch('Auth/verifyToken')
        .then((verify: boolean) => {
          if (this.isBan) {
            // アク禁
            this.$router.replace('/ban')
          } else if ((verify && this.$route.name === 'ban') || (!verify && this.token.length > 0)) {
            // token は有効だがアク禁ページにいる or token は無効だが token を持っている
            this.$router.replace('/login')
          } else if (!/(signup|login|logout)/.test(this.$route.name) && this.token.length === 0) {
            // 登録・ログインページじゃないのに token がないなら登録ページにリダイレクト(ログイン URL を許可するため)
            this.$router.replace('/signup')
          }
          return verify
        })
        .catch(err => {
          console.error(err)
          return false
        })
    },

    /**
     * 端末にアカウント情報を持っているかチェック
     */
    checkHasAuthInfoOnDevice(): Promise<boolean> {
      return this.$_secureGetAccount()
        .then(account => {
          if (account && account.userId && account.password && account.historyId) {
            return true
          } else {
            return false
          }
        })
        .catch(err => {
          console.error(err)
          return false
        })
    },

    /**
     * アプリのバージョンチェック
     */
    checkAppVersion() {
      this.$store
        .dispatch('AppInfo/checkMustUpdate', {
          os: cordova.platformId,
          version: this.$store.getters['AppInfo/appVersion']
        })
        .then(() => {
          if (this.$store.getters['AppInfo/mustUpdate'] && !this.alertCheckAppVersion) {
            this.alertCheckAppVersion = navigator.notification.alert(
              '最新のバージョンがストアで公開されています。お手数ですが、アップデートをお願いいたします。',
              () => {
                if (this.$_platformIsAndroid) {
                  window.open(this.$store.getters['AppInfo/storeUrl'], '_system')
                } else {
                  // iOS で window.open に '_self' を指定すると handleOpenURL が呼び出されてしまう
                  window.SafariViewController.show({ url: this.$store.getters['AppInfo/storeUrl'] })
                }
                return
              },
              ''
            )
          } else {
            this.alertCheckAppVersion = null
          }
        })
        .catch(err => console.error(err))
    },

    /**
     * Android アプリのリジェクトチェック
     */
    checkRejectAndroid() {
      if (!this.$_platformIsAndroid) return

      // リジェクトチェック
      this.$store
        .dispatch('AppInfo/checkRejectAndroid')
        .then(() => {
          if (this.$store.getters['AppInfo/rejectedAndroid'] && !this.alertCheckAppVersion) {
            // リジェクトされているなら別バージョンをイントール
            this.alertCheckRejectAndroid = navigator.notification.alert(
              config.MSG_REJECT_GOOGLEPLAYSTORE,
              () => {
                window.open(this.$store.getters['AppInfo/apkDownloadURL'], '_system')
                return
              },
              ''
            )
            return
          } else {
            this.alertCheckRejectAndroid = null
          }
        })
        .catch(err => {
          console.error(err)
        })
    },

    /**
     * Snackbar を表示
     */
    showSnackbar(message: string, url: string) {
      // 表示中の新規 snackbar は無視する
      if (this.isShowSnackbar) return
      this.isShowSnackbar = true

      // 初期化中 or チャット中や通話中は snackbar でを表示しない
      // android はアプリ未起動状態で10件メッセージを受信すると、起動後に10件分の snackbar が表示されてしまう
      if (
        !sessionStorage.getItem('isActiveApp') ||
        sessionStorage.getItem('isInitializing') ||
        /(chat|call)/.test(this.$route.name)
      ) {
        return
      }
      this.$snackbar.open({
        message: message,
        actionText: '確認',
        position: 'is-bottom',
        type: 'is-success',
        duration: config.SNACKBAR_DURATION,
        onAction: () => {
          this.$router.push(url)
        }
      })
      sleep(config.SNACKBAR_DURATION).then(() => {
        this.isShowSnackbar = false
      })
    },

    /**
     * router-view の key が変わると呼ばれる
     */
    handleRouterViewKeyChange(key: string) {
      // router-view の key を変更することでコンポーネントのリロード
      if (this.loadingTimer) {
        return
      }
      this.isShowLoading = true
      window.scrollTo(0, 0)

      this.$route.params.refreshRouterKey = key
      this.loadingTimer = setTimeout(() => {
        this.isShowLoading = false
        this.loadingTimer = null
      }, 300)
    },

    /**
     * チャット受信時の処理
     */
    receivedChat(notifyData: any) {
      // キューを用意しないと同時にメッセージが届いた場合などにさばけない？
      this.setHasMessageBadge(true)

      this.$store
        .dispatch('MessageList/binarySearch', {
          key: 'message_id',
          value: Number(notifyData.prev_message_id),
          reverse: true
        })
        .then((index: number) => {
          // debug
          console.log('[receivedChat] index', index)
          //
          if (index === -1 && notifyData) {
            // まだメッセージリストにないユーザなら作成する
            const name = notifyData.message.match(/(^.+)さんから(.+)$/)[1]
            const timestamp = Object.keys(notifyData.entries)[0]
            const entry = notifyData.entries[timestamp]
            const obj = entry.message || entry.gift || entry.stamp
            this.$store.commit('MessageList/unshiftEntry', {
              id: notifyData.user_id,
              message: obj.text || notifyData.message.replace(/.+さんから/, ''),
              message_id: notifyData.message_id,
              name: name,
              profile_image_url: notifyData.image,
              time: obj.time,
              timestamp: timestamp,
              unread_count: 1
            })
          } else {
            // すでにメッセージリスト存在するなら未読の increment と sort
            this.$store.commit('MessageList/incrementUnreadCount', index)
            this.sortMessages({ data: notifyData, targetIndex: index })
          }

          this.showSnackbar('新着メッセージを受信しました', '/message/' + notifyData.user_id)
        })
        .catch(err => console.error(err))
    },

    /**
     * タイムラインいいね通知受信
     */
    receivedTimelineIine(notifyData: any) {
      this.showSnackbar(notifyData.message, `/timeline/${notifyData.t_id}/iine`)
    },

    /**
     * マイページ通知受信時の処理
     */
    receivedNotice(notifyData: any) {
      this.setHasNoticeBadge(true)
      let type = 'syslog'
      if (notifyData.type === '1') type = 'news'
      const no = notifyData.no ? `/${notifyData.no}` : ''
      this.showSnackbar('新しいお知らせがあります', `/notice/${type}${no}`)
    },

    /**
     * Edge を右スワイプ
     */
    handleEdgeSwipeRight() {
      this.$router.back()
    },

    /**
     * Edge を左スワイプ
     */
    handleEdgeSwipeLeft() {
      this.$router.forward()
    }
  }
})
